import { Box } from "@mui/material";

import Log from "@/Logger";

import { DraftStatus, type FormFieldDef } from "../types";
import { mmToPt } from "../utils";
import { getValidateErrorMessage, getValidateRegex, getValidateSelection, validateType } from "../validateForm";
import { useEffect, useState, useRef } from "react";

export type FieldInputProps = {
    scale: number;
    field: FormFieldDef;
    draftStatus: DraftStatus;
    draftLocal?: string;

    /** フォームフィールドにフォーカスが入ったことを通知する */
    notifyFocusIn: (key: string) => void;
    /** フォームフィールドからフォーカスが外れたことを通知する */
    notifyFocusOut: (key: string, value?: string) => void;
};

function isEnabled(draftStatus: DraftStatus): boolean {
    return draftStatus === DraftStatus.Idle || draftStatus === DraftStatus.Editing || draftStatus === DraftStatus.Dirty || draftStatus === DraftStatus.Updated;
}
function accentColorForStatus(draftStatus: DraftStatus): string {
    switch (draftStatus) {
        case DraftStatus.Conflict: return "#FF000040";//競合中は赤色
        case DraftStatus.Updated: return "#4a91e240"; //自動更新された場合は青色
        default: return "#6b7280";
    }
}

function backgroundColorForStatus(draftStatus: DraftStatus): string {
    // switch (draft.status) {
    // case DraftStatus.Conflict: return "#FF000040";//競合中は赤色
    // case DraftStatus.Updated: return "#4a91e240"; //自動更新された場合は青色
    // default: return "#fff";
    // }
    switch (draftStatus) {
        case DraftStatus.None: return "#8884";        // 無効は灰色
        case DraftStatus.Conflict: return "#FF000040";// 競合中は赤色
        case DraftStatus.Updated: return "#c0d1ff6e"; // 自動更新された場合は青色
        case DraftStatus.Editing: return "#95e1ff60"; // 編集中は水色
        case DraftStatus.Dirty: return "#a0f4";       // 変更ありは紫色
        case DraftStatus.Sending: return "#ffef92ff"; // 送信中は黄色
        default: return "#fff";
    }
}

/**
 * フィールドの値をバリデーションします。
 * 
 * @param field バリデーションを行うフィールド定義
 * @param value バリデーションを行う値
 * @returns  {boolean} バリデーションに成功した場合true、失敗した場合falseを返す
 */
function validateFieldValue(field: FormFieldDef, value?: string): boolean {
    const logger = Log.getLogger();
    logger.ui.info('PdfViewer', 'validateFieldValue', `on ${field.key}`);

    const formRegexp = getValidateRegex(field.validateType);

    if (field.validateType && formRegexp && !formRegexp.test(value ?? "")) {
        // 入力値が正規表現にマッチしない場合はアラートを表示
        alert(`${getValidateErrorMessage(field.validateType)}: ${value}`);
        return false;
    }

    return true;
}

const CheckBoxFieldContent: React.FC<{
    field: FormFieldDef;
    draftStatus: DraftStatus;
    draftLocal?: string;
    notifyFocusIn: (key: string) => void;
    notifyFocusOut: (key: string, value?: string) => void;
}> = ({ field, draftStatus, draftLocal, notifyFocusIn, notifyFocusOut }) => {
    return (
        <label>
            <input
                type="checkbox"
                checked={draftLocal === "\u2713"}
                onChange={async (e) => {
                    let checkedValue: string | undefined = e.target.checked ? "\u2713" : "";
                    notifyFocusIn(field.key);
                    if (!validateFieldValue(field, checkedValue)) {
                        // バリデーション失敗
                        checkedValue = undefined; // 変更を破棄
                        e.target.checked = !e.target.checked; // チェック状態を元に戻す
                    }
                    notifyFocusOut(field.key, checkedValue);
                }}
                disabled={field.readonly || !isEnabled(draftStatus)}
                style={{
                    width: "90%",
                    height: "90%",
                    accentColor: accentColorForStatus(draftStatus),
                    position: "absolute",
                    zIndex: 1,
                    pointerEvents: draftStatus === DraftStatus.Conflict ? "none" : "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            />
            <span // 背景
                style={{
                    width: "100%",
                    height: "100%",
                    background: backgroundColorForStatus(draftStatus),
                    borderRadius: "5px",
                    display: "inline-block",
                    boxSizing: "border-box",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            />
        </label>
    );
}

const SelectFieldContent: React.FC<{
    field: FormFieldDef;
    draftStatus: DraftStatus;
    draftLocal?: string;
    notifyFocusIn: (key: string) => void;
    notifyFocusOut: (key: string, value?: string) => void;
}> = ({ field, draftStatus, draftLocal, notifyFocusIn, notifyFocusOut }) => {
    const prevStatus = useRef<DraftStatus>(DraftStatus.None);
    const elemRef = useRef<HTMLSelectElement>(null);

    // 編集不可中にフォーカスが当たっていた場合は、送信完了後に再フォーカスする
    useEffect(() => {
        if (!isEnabled(prevStatus.current)) {
            if (elemRef.current && document.activeElement === elemRef.current) {
                notifyFocusIn(field.key);
            }
        }
        return () => { prevStatus.current = draftStatus; };
    }, [draftStatus, field.key, notifyFocusIn]);

    return (

        <select
            ref={elemRef}
            value={draftLocal ?? ""}
            onFocus={() => {
                notifyFocusIn(field.key);
            }}
            onChange={async (e) => {
                let value: string | undefined = e.target.value;
                if (!validateFieldValue(field, value)) {
                    // バリデーション失敗
                    value = undefined; // 変更を破棄
                    e.target.value = draftLocal ?? ""; // 入力状態を元に戻す
                    // 再フォーカスする
                    setTimeout(() => e.target.focus(), 0);
                    return;
                }
                Log.getLogger().ui.info('PdfViewer', 'SelectFieldContent', `on ${field.key} change to ${value}`);
                notifyFocusOut(field.key, value);
            }}
            onBlur={async (e) => {
                if (draftStatus === DraftStatus.Editing)
                    notifyFocusOut(field.key);
            }}
            disabled={field.readonly || !isEnabled(draftStatus)}
            style={{
                width: "100%",
                height: "100%",
                padding: "4px",
                fontSize: `calc((100% - 2px) * 0.9)`,
                border: "1px solid #ccc",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                background: backgroundColorForStatus(draftStatus),
                position: "absolute",
                zIndex: 1,
                pointerEvents: draftStatus === DraftStatus.Conflict ? "none" : "auto",
            }}
        >
            {getValidateSelection(field.validateType)?.map(option => (
                <option key={option} value={option}>
                    {option}
                </option>
            ))}
        </select>
    );
}
const TextFieldContent: React.FC<{
    field: FormFieldDef;
    draftStatus: DraftStatus;
    draftLocal?: string;
    notifyFocusIn: (key: string) => void;
    notifyFocusOut: (key: string, value?: string) => void;
}> = ({ field, draftStatus, draftLocal, notifyFocusIn, notifyFocusOut }) => {
    const prevStatus = useRef<DraftStatus>(DraftStatus.None);
    const elemRef = useRef<HTMLInputElement>(null);
    const [innerValue, setInnerValue] = useState(draftLocal ?? '');

    useEffect(() => { // draftLocalが外部から更新された場合に内部値を更新する
        setInnerValue(draftLocal ?? '');
    }, [draftLocal]);

    // 編集不可中にフォーカスが当たっていた場合は、送信完了後に再フォーカスする
    useEffect(() => {
        if (!isEnabled(prevStatus.current)) {
            if (elemRef.current && document.activeElement === elemRef.current) {
                notifyFocusIn(field.key);
            }
        }
        return () => { prevStatus.current = draftStatus; };
    }, [draftStatus, field.key, notifyFocusIn]);

    return (
        <input
            ref={elemRef}
            type="text"
            value={innerValue}
            onFocus={() => {
                if (!isEnabled(draftStatus)) return;
                notifyFocusIn(field.key);
            }}
            onChange={(e) => {
                if (!isEnabled(draftStatus)) return;
                setInnerValue(e.target.value); // 内部値のみ更新
            }}
            onBlur={(e) => {
                if (!isEnabled(draftStatus)) return;
                let value: string | undefined = e.target.value;
                if (!validateFieldValue(field, value)) {
                    // バリデーション失敗
                    setInnerValue(draftLocal ?? ""); // 変更を破棄,入力状態を元に戻す
                    // 再フォーカスする
                    setTimeout(() => e.target.focus(), 0);
                    return;
                }
                setInnerValue(value); // 内部値を更新
                notifyFocusOut(field.key, value); // 更新を通知
            }}
            onKeyDown={e => {
                if (e.key === "Enter") {
                    (e.target as HTMLInputElement).blur();
                }
            }}
            readOnly={field.readonly || !isEnabled(draftStatus)}
            style={{
                width: "100%",
                height: "100%",
                padding: "4px",
                fontSize: `calc((100% - 2px) * 0.9)`,
                border: "1px solid #ccc",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                background: backgroundColorForStatus(draftStatus),
                position: "absolute",
                zIndex: 1,
                pointerEvents: draftStatus === DraftStatus.Conflict ? "none" : "auto",
            }}
        />
    );
}

/**  */
export const FieldInput: React.FC<FieldInputProps> = ({ scale, field, draftStatus, draftLocal, notifyFocusIn, notifyFocusOut }) => {
    return (<Box
        key={`fi-${field.key}`}
        sx={{
            position: "absolute",
            left: mmToPt(field.rect.x) * scale,
            top: mmToPt(field.rect.y) * scale,
            width: mmToPt(field.rect.w) * scale,
            height: mmToPt(field.rect.h) * scale,
            zIndex: 1,
            pointerEvents: "none", // 吹き出し以外はpointerイベントを通さない
        }}
    >
        <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
            {field.validateType === validateType.onlyCheck // is checkbox ?
                ? (<CheckBoxFieldContent key={`chk-${field.key}`} field={field} draftStatus={draftStatus} draftLocal={draftLocal} notifyFocusIn={notifyFocusIn} notifyFocusOut={notifyFocusOut} />)
                : getValidateSelection(field.validateType) // is select ?
                    // トリアージや男女などはプルダウンで入力する
                    ? (<SelectFieldContent key={`sel-${field.key}`} field={field} draftStatus={draftStatus} draftLocal={draftLocal} notifyFocusIn={notifyFocusIn} notifyFocusOut={notifyFocusOut} />)
                    // 選択肢のもの以外はテキストボックスに入れる
                    : (<TextFieldContent key={`txt-${field.key}`} field={field} draftStatus={draftStatus} draftLocal={draftLocal} notifyFocusIn={notifyFocusIn} notifyFocusOut={notifyFocusOut} />)}
        </Box>
    </Box>);
}
